import React, { useMemo, useState } from "react";

import { Box } from "@chakra-ui/react";
import { ReactComponent as ArrowRightIcon } from "assets/icons/arrow-right.svg";
import { ReactComponent as CloseIcon } from "assets/icons/close-icon-in-grey-bg.svg";
import { ReactComponent as InfoOutlineIcon } from "assets/icons/info-outline.svg";
import { ReactComponent as RadioCheckedIcon } from "assets/icons/radio-icon-checked.svg";
import { ReactComponent as RadioDefaultIcon } from "assets/icons/radio-icon-default.svg";
import {
  HALF_A_SECOND,
  NUMBER_OF_HOURS,
  NUMBER_OF_MILLISECONDS,
  NUMBER_OF_SECONDS,
} from "utils/constants";

import { Button } from "ui/button";
import { customErrorToast } from "ui/custom-toast";
import DateInput from "ui/date-input";
import { Modal } from "ui/modal";
import { defaultModalCss } from "ui/modal/styles";
import { Heading3 } from "ui/typography";

import {
  IGenerateAccountStatementFormData,
  IGenerateAccountStatementFormDataRecipent,
  IGenerateAccountStatementModalProps,
  IGenerateAccountStatementRecipientOption,
} from "./types";

const recipients: IGenerateAccountStatementRecipientOption[] = [
  {
    value: "email",
    label: "Email to Customer",
  },
  {
    value: "manual",
    label: "Download Manually",
  },
];

export const DEFAULT_GENERATE_ACCOUNT_STATEMENT_FORM_DATA: IGenerateAccountStatementFormData =
  {
    startDate: null,
    endDate: null,
    recipient: "",
  };

const MAX_MONTH_RANGE = 6;
export const GenerateAccountStatementModal = (
  props: IGenerateAccountStatementModalProps
) => {
  const {
    isOpen,
    setIsOpen,
    handleToggleModal,
    handleGenerateAccountStatement,
    isLoading,
    formData,
    setFormData,
  } = props;
  const [timeframeExceedsLimit, setTimeframeExceedsLimit] = useState(false);

  const handleDateRangeChange = (
    value: Date | null,
    name: "startDate" | "endDate"
  ) => {
    setFormData((prev) => {
      if (
        name === "startDate" &&
        prev.endDate &&
        value &&
        value > prev.endDate
      ) {
        return prev;
      }

      if (
        name === "endDate" &&
        prev.startDate &&
        value &&
        value < prev.startDate
      ) {
        return prev;
      }

      const updatedFormData = {
        ...prev,
        [name]: value,
      };
      // Check if the selected date range exceeds 6 months
      if (updatedFormData.startDate && updatedFormData.endDate) {
        const timeDiff =
          updatedFormData.endDate.getTime() -
          updatedFormData.startDate.getTime();
        const monthDiff =
          timeDiff /
          (NUMBER_OF_MILLISECONDS *
            NUMBER_OF_SECONDS *
            NUMBER_OF_SECONDS *
            NUMBER_OF_HOURS *
            HALF_A_SECOND); // Approximate month difference
        setTimeframeExceedsLimit(monthDiff > MAX_MONTH_RANGE);
      }
      return updatedFormData;
    });
  };

  const handleRecipientChange = (
    value: IGenerateAccountStatementFormDataRecipent
  ) => {
    setFormData({
      ...formData,
      recipient: value,
    });
  };

  const handleCloseModal = () => {
    setFormData(DEFAULT_GENERATE_ACCOUNT_STATEMENT_FORM_DATA);
    handleToggleModal();
  };

  const onGenerateStatementClick = async () => {
    if (timeframeExceedsLimit) {
      customErrorToast({
        title: "Validation",
        message: `Time frame exceeds ${MAX_MONTH_RANGE} months`,
      });
      return;
    }
    await handleGenerateAccountStatement();
  };

  const isGenerateBtnDisabled = useMemo(() => {
    const { startDate, endDate, recipient } = formData;
    return !startDate || !endDate || !recipient;
  }, [formData]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      modalCss={{
        ...defaultModalCss,
        width: "501px",
        height: "533px",
        padding: "24px",
        gap: "32px",
        boxShadow: "0px 0px 7.4px 0px #0000000D",
      }}
      allowOutsideClick={false}
      position="center"
    >
      <Box
        className="flex items-center h-6 justify-between"
        css={{
          position: "relative",

          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            bottom: "-12px",
            width: "100%",
            height: "1px",
            backgroundColor: "#F3F4F6",
          },
        }}
      >
        <Heading3
          fontWeight="700"
          lineHeight="27.32px"
          textColor="#1A202C"
        >
          Generate Account Statement
        </Heading3>
        <CloseIcon
          className="cursor-pointer"
          onClick={handleCloseModal}
        />
      </Box>
      <Box className="flex flex-col h-[349px] gap-6">
        <Box className="flex flex-col gap-2 h-[67px]">
          <p className="font-semibold text-[16px] leading-[20px] text-[#1A202C]">
            Select a Timeframe
          </p>
          <Box className="flex gap-4 items-center">
            <Box
              className="inline-flex items-center gap-2 h-10 w-[208px]"
              css={{
                "& input": {
                  height: "41px !important",
                },
              }}
            >
              <DateInput
                name="startDate"
                value={formData.startDate}
                onChange={(value) => {
                  handleDateRangeChange(value, "startDate");
                }}
                placeholder="Start Date"
                preventOpenOnFocus
              />
            </Box>
            <Box>
              <ArrowRightIcon />
            </Box>
            <Box
              className="inline-flex items-center gap-2 h-10 w-[208px]"
              css={{
                "& input": {
                  height: "41px !important",
                },
              }}
            >
              <DateInput
                name="endDate"
                value={formData.endDate}
                onChange={(value) => {
                  handleDateRangeChange(value, "endDate");
                }}
                placeholder="End Date"
                preventOpenOnFocus
              />
            </Box>
          </Box>
        </Box>
        <Box className="flex flex-col gap-2 h-[170px]">
          <p className="font-semibold text-[16px] leading-[20px] text-[#1A202C]">
            How do you want it?
          </p>
          <Box className="flex flex-col gap-2">
            {recipients.map(({ value, label }, idx) => (
              <Box
                css={{
                  borderRadius: "8px",
                  border: `1px solid ${formData.recipient !== value ? "#ECF0F1" : "#85B5BA"}`,
                  backgroundColor: `${formData.recipient !== value ? "#ffffff" : "#00535C14"}`,
                  padding: "24px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
                key={idx}
                onClick={() => handleRecipientChange(value)}
              >
                <p className="text-[#101828] font-semibold text-[14px] leading-[19.12px]">
                  {label}
                </p>
                {formData.recipient !== value ? (
                  <RadioDefaultIcon
                    className="cursor-pointer"
                    onClick={() => handleRecipientChange(value)}
                  />
                ) : (
                  <RadioCheckedIcon
                    className="cursor-pointer"
                    onClick={() => handleRecipientChange(value)}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
        <Box className="bg-[#FEF5E7] border border-[#FFEFD8] rounded-2xl p-3 flex items-center gap-2 h-16">
          <InfoOutlineIcon className="flex-shrink-0" />
          <p className="text-sm text-[#1A202C]">
            You can only generate an account statement for a maximum period
            of&nbsp;
            {MAX_MONTH_RANGE}&nbsp;months.
          </p>
        </Box>
      </Box>
      <Box
        css={{
          height: "48px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outline"
          textColor="#00535C"
          border="1px solid #00535C"
          className={`w-[90px] text-[16px] leading-[20px] h-12 ${isLoading ? "cursor-not-allowed opacity-30" : "cursor-pointer"}`}
          onClick={isLoading ? undefined : handleCloseModal}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="solid"
          textColor="#ffffff"
          border="1px solid #00535C"
          className={`w-[205px] text-[16px] leading-[20px] h-12 ${isLoading || isGenerateBtnDisabled ? "cursor-not-allowed opacity-30" : "cursor-pointer"}`}
          onClick={
            isLoading || isGenerateBtnDisabled
              ? undefined
              : onGenerateStatementClick
          }
          disabled={isLoading || isGenerateBtnDisabled}
          loading={isLoading}
          loadingText="Generating..."
        >
          Generate Statement
        </Button>
      </Box>
    </Modal>
  );
};
